class TootoCollectorStories extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-tooto-stories-viewport]');
    this.cards = [...this.querySelectorAll('[data-tooto-story-card]')];
    this.previousButton = this.querySelector('[data-tooto-stories-previous]');
    this.nextButton = this.querySelector('[data-tooto-stories-next]');
    this.pageButtons = [...this.querySelectorAll('[data-tooto-stories-page]')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobile = window.matchMedia('(max-width: 767px)');
    this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (!this.viewport || !this.cards.length) return;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);

    this.previousButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));
    this.pageButtons.forEach((button, pageIndex) => {
      button.addEventListener('click', () => this.goToIndex(this.mobileStartIndex + pageIndex));
    });
    this.querySelectorAll('[data-tooto-story-mute]').forEach((button) => {
      const video = button.closest('[data-tooto-story-card]')?.querySelector('[data-tooto-story-video]');
      if (!video) return;

      button.addEventListener('pointerdown', (event) => event.stopPropagation());
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleMute(button);
      });
      video.addEventListener('volumechange', () => this.updateMuteButton(button, video));
      this.updateMuteButton(button, video);
    });
    this.cards.forEach((card, index) => {
      if (!card.querySelector('[data-tooto-story-video]')) return;
      card.addEventListener('pointerenter', () => {
        this.hoveredIndex = index;
        this.playVideo(index);
      });
      card.addEventListener('pointerleave', () => {
        if (this.hoveredIndex === index) this.hoveredIndex = null;
        if (this.focusedIndex !== index) this.pauseVideo(index);
      });
      card.addEventListener('focusin', () => {
        this.focusedIndex = index;
        this.playVideo(index);
      });
      card.addEventListener('focusout', () => {
        if (this.focusedIndex === index) this.focusedIndex = null;
        if (this.hoveredIndex !== index) this.pauseVideo(index);
      });
    });

    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibility);

    this.sectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: 0.25 });
    this.sectionObserver.observe(this);
    this.handleResize();
  }

  disconnectedCallback() {
    this.viewport?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.sectionObserver?.disconnect();
    window.cancelAnimationFrame(this.scrollFrame);
    window.cancelAnimationFrame(this.resizeFrame);
    window.clearTimeout(this.scrollEndTimer);
    this.pauseVideos();
  }

  handleResize() {
    const isMobile = this.mobile.matches;
    const enteredMobile = isMobile && this.layoutMode !== 'mobile';
    this.mobileStartIndex = isMobile ? Math.floor(this.cards.length / 2) : 0;
    this.layoutMode = isMobile ? 'mobile' : 'wide';

    window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      if (enteredMobile) this.goToIndex(this.mobileStartIndex, false);
      else this.updateActiveFromScroll();
    });
  }

  handleScroll() {
    window.cancelAnimationFrame(this.scrollFrame);
    window.clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = window.setTimeout(() => {
      this.programmaticScroll = false;
      this.updateActiveFromScroll();
    }, 100);

    if (this.programmaticScroll) return;
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.updateActiveFromScroll();
    });
  }

  updateActiveFromScroll() {
    const closestIndex = this.getClosestIndex();
    this.navigationIndex = closestIndex;
    this.setActiveIndex(closestIndex);
  }

  updateControls() {
    const firstCard = this.cards[0];
    const lastCard = this.cards[this.cards.length - 1];
    if (!firstCard || !lastCard) return;

    const viewportLeft = this.viewport.scrollLeft;
    const viewportRight = viewportLeft + this.viewport.clientWidth;
    const firstCardLeft = firstCard.offsetLeft;
    const lastCardRight = lastCard.offsetLeft + lastCard.offsetWidth;
    const tolerance = 2;

    if (this.previousButton) {
      this.previousButton.disabled = firstCardLeft >= viewportLeft - tolerance;
    }
    if (this.nextButton) {
      this.nextButton.disabled = lastCardRight <= viewportRight + tolerance;
    }
  }

  getClosestIndex() {
    const viewportCenter = this.viewport.scrollLeft + (this.viewport.clientWidth / 2);
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
      const distance = Math.abs(cardCenter - viewportCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  }

  step(direction) {
    this.updateControls();
    if (direction < 0 && this.previousButton?.disabled) return;
    if (direction > 0 && this.nextButton?.disabled) return;

    const currentIndex = this.programmaticScroll
      ? (this.navigationIndex ?? 0)
      : this.getClosestIndex();
    const nextIndex = Math.max(0, Math.min(currentIndex + direction, this.cards.length - 1));
    this.goToIndex(nextIndex);
  }

  goToIndex(index, smooth = true) {
    const safeIndex = Math.max(0, Math.min(index, this.cards.length - 1));
    const card = this.cards[safeIndex];
    const left = card.offsetLeft - ((this.viewport.clientWidth - card.offsetWidth) / 2);
    this.navigationIndex = safeIndex;
    this.programmaticScroll = smooth && !this.reducedMotion.matches;
    this.viewport.scrollTo({ left, behavior: smooth && !this.reducedMotion.matches ? 'smooth' : 'auto' });
    this.setActiveIndex(safeIndex);
  }

  setActiveIndex(index) {
    this.activeIndex = index;
    this.updateControls();

    const pageIndex = Math.max(0, Math.min(index - this.mobileStartIndex, this.pageButtons.length - 1));
    this.pageButtons.forEach((button, buttonIndex) => {
      const active = buttonIndex === pageIndex;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
    });

    this.syncVideos();
  }

  toggleMute(button) {
    const video = button.closest('[data-tooto-story-card]')?.querySelector('[data-tooto-story-video]');
    if (!video) return;
    video.muted = !video.muted;
    this.updateMuteButton(button, video);
  }

  updateMuteButton(button, video) {
    const soundIsOn = !video.muted && video.volume > 0;
    button.setAttribute('aria-pressed', String(soundIsOn));
  }

  handleIntersection(entries) {
    this.isVisible = entries[0]?.isIntersecting ?? false;
    this.syncVideos();
  }

  handleVisibility() {
    this.syncVideos();
  }

  playVideo(index) {
    if (document.hidden) return;

    this.cards.forEach((card, cardIndex) => {
      const video = card.querySelector('[data-tooto-story-video]');
      if (!video) return;

      if (cardIndex !== index) {
        video.pause();
        return;
      }

      if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
      const playback = video.play();
      if (playback?.catch) {
        playback.catch(() => {
          video.muted = true;
          const muteButton = card.querySelector('[data-tooto-story-mute]');
          if (muteButton) this.updateMuteButton(muteButton, video);
          video.play().catch(() => {});
        });
      }
    });
  }

  pauseVideo(index) {
    this.cards[index]?.querySelector('[data-tooto-story-video]')?.pause();
  }

  syncVideos() {
    const interactionIndex = this.focusedIndex ?? this.hoveredIndex;
    const userInitiated = interactionIndex !== null && interactionIndex !== undefined;
    const playbackIndex = userInitiated
      ? interactionIndex
      : (this.hoverCapable.matches ? null : this.activeIndex);

    const shouldPlay = playbackIndex !== null
      && playbackIndex !== undefined
      && !document.hidden
      && (userInitiated || (this.isVisible && !this.reducedMotion.matches));

    if (shouldPlay) this.playVideo(playbackIndex);
    else this.pauseVideos();
  }

  pauseVideos() {
    this.querySelectorAll('[data-tooto-story-video]').forEach((video) => video.pause());
  }
}

if (!customElements.get('tooto-collector-stories')) {
  customElements.define('tooto-collector-stories', TootoCollectorStories);
}
